import React, { useState } from 'react';
import { Button } from '../../ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { FollowUpForm } from './FollowUpForm';
import { CompleteNote } from './CompleteNote';
import { dueBucket, formatDue } from '../../../utils/followUpDates';

const BUCKET_STYLE = {
  overdue: 'text-red-600 bg-red-50 border-red-100',
  today: 'text-amber-600 bg-amber-50 border-amber-100',
  upcoming: 'text-blue-600 bg-blue-50 border-blue-100'
};

export const FollowUpRow = ({ item, onSave, onComplete, onReopen, onDelete }) => {
  const [mode, setMode] = useState(null); // 'edit' | 'complete'
  const [busy, setBusy] = useState(false);
  const done = item.is_completed;

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'edit') {
    return (
      <div className="bg-white p-3 rounded-xl border border-slate-200">
        <FollowUpForm
          initial={item}
          submitLabel="Save changes"
          onCancel={() => setMode(null)}
          onSubmit={async (v) => {
            await onSave(item, v);
            setMode(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded border ${done ? 'text-slate-500 bg-slate-50 border-slate-200' : BUCKET_STYLE[dueBucket(item.due_date)]}`}>
            {formatDue(item.due_date)}
          </span>
          <p className="text-sm font-medium text-slate-800 mt-1.5 break-words">{item.note || 'No specific note provided.'}</p>
          {item.moments?.title && <p className="text-xs text-slate-500 mt-1">From Moment: {item.moments.title}</p>}
          {done && (
            <p className="text-xs text-slate-500 mt-1">
              Completed {item.completed_at ? new Date(item.completed_at).toLocaleDateString() : ''}
              {item.completion_note ? ` · ${item.completion_note}` : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {done ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => run(() => onReopen(item))}>Reopen</Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setMode('edit')} title="Edit or reschedule">
                <SafeIcon icon={FiIcons.FiEdit2} />
              </Button>
              <Button variant="ghost" size="sm" disabled={busy} className="text-green-600 hover:bg-green-50" onClick={() => setMode('complete')} title="Complete">
                <SafeIcon icon={FiIcons.FiCheck} />
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" disabled={busy} className="text-slate-400 hover:text-red-500" onClick={() => run(() => onDelete(item))} title="Delete">
            <SafeIcon icon={FiIcons.FiTrash2} />
          </Button>
        </div>
      </div>
      {mode === 'complete' && (
        <CompleteNote
          onCancel={() => setMode(null)}
          onConfirm={async (n) => {
            await onComplete(item, n);
            setMode(null);
          }}
        />
      )}
    </div>
  );
};